import React, { useEffect, useState } from "react";
import useEditLoad from "../../hooks/useEditLoad";
import EditLoadGate from "../../components/editLoadGate";
import Modal from "react-modal";
import axios from "axios";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import Select from "../../components/select";
import { BsFiletypeCsv } from "react-icons/bs";

import { uploadMediaFile } from "../../utills/fileUpload";
import PreviewImage from "../../components/image/previewImage";
import LanguageSelector from "../../components/languageSelector";
import BulkUploadAuthor from "./bulkUpload";
import Button from "../../components/button";

import { fetchAllGenresByLangOptions, fetchAllProfessionOptions, convertToCloudFrontUrl , pastePlainText } from "../../helper";
import { useLanguage } from "../../context/languageContext";
import { usePermissions } from "../../context/permissionContext";
import { useAlreadyExists } from "../../components/confirmDialog";


const AddEditPopup = (props) => {
    const { submitLabel } = usePermissions();
  const{ languages, selectedLang, setSelectedLang, adminInfo, initialLangRef, fallbackLang } = useLanguage();
  const showAlreadyExists = useAlreadyExists();
  const { t } = useTranslation();

  const { 
    isAddAuthorModalVisible,
    setIsAddAuthorModalVisible,
    isEditAuthorModalVisible,
    handleCloseModal,
    authorId,
    onSuccess
  } = props;    
  
  const createInitialLangValues = (lang) => ({ 
    langCode: lang, 
    authorName: "", 
    profession: [], 
    // booksWritten: "", 
    genres: [], 
    about: "",
    type: "NonFiction"
  });

  const createInitialTranslationsArr = () => languages.map((lang) => createInitialLangValues(lang));

  const [values, setValues] = useState({
    image: {},
    translations: createInitialTranslationsArr(),
  });

  const [particularAuthorsData, setParticularAuthorData] = useState(null);
  const [recommendeGenres, setRecommendeGenres] = useState([]);
  const [isBulkUploadAuthorPopupVisible, setIsBulkUploadAuthorPopupVisible] = useState(false);

  const [editMediaError, setEditMediaError] = useState("");
  const [errors, setErrors] = useState({});  
  const [isSubmit, setIsSubmit] = useState(false);
  // Edit shows, and saves, only the author that was opened — see hooks/useEditLoad.
  const load = useEditLoad();
  const editReady = !isEditAuthorModalVisible || load.isLoadedFor(authorId);

  useEffect(() => {
    if (isEditAuthorModalVisible && authorId) {
      load.begin(authorId);
      initialLangRef.current = false;
      setValues({
        image: {},
        translations: createInitialTranslationsArr(),
      });
      handleFetchParticularAuthor(String(authorId));
    }
    if (!isEditAuthorModalVisible) load.reset();
  }, [isEditAuthorModalVisible, authorId]);

  useEffect(() => {
    if ((isAddAuthorModalVisible || isEditAuthorModalVisible) && fallbackLang) {
      setSelectedLang(fallbackLang);
    }
  }, [isAddAuthorModalVisible, isEditAuthorModalVisible]);

  useEffect(() => {
    if (isAddAuthorModalVisible) {
      setValues({
        image: {},
        translations: createInitialTranslationsArr(),
      });
      fetchAllBooksAndGenresData();
    }
  }, [isAddAuthorModalVisible]);

  const handleFetchParticularAuthor = async (id) => {
    try {
      const headers = { Authorization: `Bearer ${adminInfo.token}` };
      const { data: response } = await axios.get(`${process.env.REACT_APP_API_ROOT}/api/admin/favorite-author/get/particular/${id}`, { headers });

      if (response?.status && load.accepts(id, response.data)) {
        setParticularAuthorData(response.data);
        await fetchAllBooksAndGenresData();
      } else {
        load.fail(id, response?.message || "");
      }
    } catch (error) {
      console.error("handleFetchParticularAuthor error:", error);
      load.fail(id, error);
    }
  };

  useEffect(() => {
    if (particularAuthorsData && particularAuthorsData.translations && !initialLangRef.current) {
      const mergedTranslations = languages.map((lang) => {
        return (
          particularAuthorsData.translations.find((t) => t.langCode === lang) || {
            langCode: lang,
            authorName: particularAuthorsData?.authorName || "",
            about: particularAuthorsData?.about || "",
            profession: particularAuthorsData?.profession || [],
            // booksWritten: particularAuthorsData?.booksWritten || "",
            genres: particularAuthorsData?.genres || [],
            type: particularAuthorsData?.type,
          }
        );
      });

      // Normalize author image: convert S3 URLs to CloudFront URLs
      const normalizeAuthorImage = (image) => {
        if (!image || !image.uploadedImageUrl) {
          return image || {};
        }
        return {
          ...image,
          uploadedImageUrl: convertToCloudFrontUrl(image.uploadedImageUrl)
        };
      };

      setValues((prevValues) => ({
        ...prevValues,
        image: normalizeAuthorImage(particularAuthorsData.image),
        translations: mergedTranslations,
      }));

      initialLangRef.current = true;
      load.loaded(particularAuthorsData);
    }
  }, [isEditAuthorModalVisible, particularAuthorsData, languages]);

  const handleUploadMedia = async (event) => {
    const result = await uploadMediaFile(event, true);

    if (typeof result === "string") {
      setEditMediaError(result);
    } else if (typeof result === "object" && result.length > 0) {
      setValues((prevValues) => ({
        ...prevValues,
        image: result[0]?.data,
      }));
    }
  };

  const handleUpdateTranslationField = (fieldName, value, langCode) => {
    setValues((prevValues) => {
      const updatedTranslations = prevValues.translations?.map((translation) =>
        translation.langCode === langCode
          ? {
              ...translation,
              [fieldName]: value,
              ...(fieldName === "type" && { genres: [] }),
            }
          : translation
      );

      return { 
        ...prevValues,
        translations: updatedTranslations,
      };
    });
  };

  const handleSelectLanguage = (language) => {
    setSelectedLang(language);

    setValues((prevValues) => {
      const isLanguagesExists = prevValues.translations.some((lang) => lang.langCode === language);

      if (isLanguagesExists) {
        return { ...prevValues };
      }
  
      return {
        ...prevValues,
        translations: [...prevValues.translations, createInitialLangValues(language)],
      };
    });
  };

  const handleGetTranslationByLang = (langCode) => {
    return values.translations?.find((lang) => lang.langCode === langCode) || {};
  };

  // The catalogue is fetched now rather than bundled, so it is state and
  // refetches when the editing language changes — the labels are per
  // language, and the ids are the catalogue's own.
  const [fieldOptions, setFieldOptions] = useState([]);

  useEffect(() => {
    if (!adminInfo?.token) return;

    let alive = true;

    fetchAllProfessionOptions(adminInfo.token, selectedLang).then((options) => {
      if (alive) setFieldOptions(options);
    });

    return () => {
      alive = false;
    };
  }, [adminInfo?.token, selectedLang]);

  const fetchAllBooksAndGenresData = async () => {
    const genresOptions = await fetchAllGenresByLangOptions(selectedLang, adminInfo.token);

    const options = genresOptions.map((genre) => ({
      label: genre.genreName,
      value: genre.genreId,
      type: genre.type,
    }));

    setRecommendeGenres(options);
  };

  const handleClose = () => {
    handleCloseModal();
    setErrors({});
    setEditMediaError("");
    setParticularAuthorData(null);
    setValues({
      image: {},
      translations: createInitialTranslationsArr()
    });
  };

  const validateFields = (value) => {
    let newErrors = {};
    const translation = value?.translations?.find(t => t.langCode === selectedLang);

    if (!translation.authorName ||translation.authorName === "") {
      newErrors.authorName = "Please enter an author name";
    }

    if (
      !translation.profession || 
      translation.profession.some((lang) => lang.professionName === "" || lang.professionName.trim === "") || 
      translation.profession.length === 0
    ) {
      newErrors.profession = "Please provide the profession or field name of the author";
    }

    // if (!translation.booksWritten || translation.booksWritten === "") {
    //   newErrors.booksWritten = "Please enter a name of a book";
    // }

    if (
      !translation.genres || 
      translation.genres.some((lang) => lang.genreName === "" || lang.genreName.trim === "") || 
      translation.genres.length === 0
    ) {
      newErrors.genres = "Please add genre name";
    }

    if (!translation.about || translation.about === "") {
      newErrors.about = "Please write some words about author";
    }

    // if (!value.image.uploadedImageUrl) {
    //   if (!values.image.uploadedImageUrl) {
    //     newErrors.image = "Please upload an image";
    //   } else if (!value.image.uploadedImageUrl) {
    //     newErrors.image = "Please upload an image";
    //   }
    //   newErrors.image = "Please upload an image";
    // }

    return newErrors;
  };

  const handleAddAndUpdateValues = async () => {
    // Never save an edit form that is not showing this author.
    if (!editReady) return;
    const newErrors = validateFields(values);

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    try {
      setIsSubmit(true);
      const headers = { Authorization: `Bearer ${adminInfo.token}` };
      const url = isEditAuthorModalVisible
        ? `${process.env.REACT_APP_API_ROOT}/api/admin/favorite-author/update/${authorId}`
        : `${process.env.REACT_APP_API_ROOT}/api/admin/favorite-author/add`;

      const method = isEditAuthorModalVisible ? "put" : "post";
      const { data: response } = await axios[method](url, { values }, { headers });

      if (response.status) {
        setErrors({});
        onSuccess();
        handleCloseModal();
        setParticularAuthorData(null);
        toast.success(response?.message);
        setValues({
          image: {},
          translations: createInitialTranslationsArr(),
        });
        setIsSubmit(false);
      }
    } catch (error) {
      console.error("handleAddAndUpdateValues catch error::", error);
      const errorMessage = error.response?.data?.message || error.response?.data?.error;
      if (!showAlreadyExists(error)) toast.error(errorMessage);
    } finally {
      setIsSubmit(false);
    }
  };

  const handleShowBulkUploadAuthorPopup = () => {
    setIsBulkUploadAuthorPopupVisible(true);
    setIsAddAuthorModalVisible(false);
  };

  const handleCloseBulkUploadAuthorPopup = () => {
    setIsBulkUploadAuthorPopupVisible(false);
  };

  return (
    <React.Fragment>
    <Modal
      isOpen={isAddAuthorModalVisible || isEditAuthorModalVisible}
      onRequestClose={handleClose}
      className="modal_body_lg"
      overlayClassName="modal_overlay"
    >
      <div className="box_wrapper">
        <div className="d-flex justify-content-between fw-bold mb-3">
            <h4 className="text-center">{isEditAuthorModalVisible ? "Edit Favorite Author" : " Add Favorite Author"}</h4>
            {isAddAuthorModalVisible && <button type="button" className="btn btn-outline-dark" onClick={handleShowBulkUploadAuthorPopup}>Add Favorite Authors in Bulk through CSV <BsFiletypeCsv /></button>}
            {isEditAuthorModalVisible && <LanguageSelector onSelectLanguage={handleSelectLanguage}/>}
        </div>
        <EditLoadGate ready={editReady} error={load.loadError}>
        <div className="modal_content_lg">
          <div className="row my-3">
            <div className="col-sm-12 col-md-4 mb-3">
              <label className="label">{t("summary.authorName")}:</label>
              <input
                className="form_control"
                placeholder={`${t("general.enter")} ${t("summary.authorName")}...`}
                type="text"
                name="authorName"
                value={handleGetTranslationByLang(selectedLang).authorName}
                onChange={(e) => handleUpdateTranslationField(e.target.name, e.target.value, selectedLang)}
              />
              {errors && errors.authorName && <p className="error-message d-block">{errors.authorName}</p>}
            </div>
            <div className="col-sm-12 col-md-4 mb-3">
              <label className="label">{t("recommender.professionField")}:</label>
              <Select
                className="form-control-md fw-medium p-0"
                isMulti
                closeMenuOnSelect={false}
                name="profession"
                onChange={(selectedOptions) => {
                  const updatedGenres = selectedOptions.map((option) => ({ professionName: option.label, professionId: option.value }));
                  handleUpdateTranslationField("profession", updatedGenres, selectedLang);
                }}
                value={handleGetTranslationByLang(selectedLang)?.profession?.map((option) => ({ label: option.professionName, value: option.professionId })) || []}
                options={fieldOptions}
              />
              {errors && errors.profession && <p className="error-message d-block">{errors.profession}</p>}
            </div>
            {/* <div className="col-sm-12 col-md-6 mb-3">
              <label className="label">{t("summary.bookName")}:</label>
              <input
                className="form_control"
                placeholder={`${t("general.enter")} ${t("summary.bookName")}...`}
                type="text"
                name="booksWritten"
                value={handleGetTranslationByLang(selectedLang).booksWritten}
                onChange={(e) => handleUpdateTranslationField(e.target.name, e.target.value, selectedLang)}
              />
              {errors && errors.booksWritten && <p className="error-message d-block">{errors.booksWritten}</p>}
            </div> */}
              <div className="col-sm-12 col-md-4 mb-3">
                <div className="d-flex gap-2">
                  <label className="label">{t("general.genre")}: </label>
                  {["Fiction", "NonFiction"].map((genre) => (
                      <div key={genre} className="form-label d-flex align-items-center text-muted">
                        {t(`general.${genre}`)}
                        <input
                          className="ms-1"
                          type="radio"
                          name="type"
                          value={genre}
                          checked={handleGetTranslationByLang(selectedLang).type === genre}
                          onChange={(e) => handleUpdateTranslationField(e.target.name, e.target.value, selectedLang)}
                          style={{ width: "16px", height: "16px" }}
                        />
                      </div>
                  ))}
                </div>
                <Select
                  className="form-control-md fw-medium p-0"
                  isMulti
                  closeMenuOnSelect={false}
                  name="genres"
                  placeholder={`Select ${handleGetTranslationByLang(selectedLang)?.type || "genres"}...`}
                  onChange={(selectedOptions) => {
                    const updatedGenres = selectedOptions.map((option) => ({ genreName: option.label, genreId: option.value }));
                    handleUpdateTranslationField("genres", updatedGenres, selectedLang);
                  }}
                  value={handleGetTranslationByLang(selectedLang)?.genres?.map((genre) => ({ label: genre.genreName, value: genre.genreId })) || []}
                  options={recommendeGenres?.filter((genre) => genre.type === handleGetTranslationByLang(selectedLang)?.type) || []}
                />
                {errors && errors.genres && <p className="error-message d-block">{errors.genres}</p>}
              </div>
            <div className="col-sm-12 col-md-8 mb-3">
              <label className="label">{t("general.about")}:
                {" "}<span className="text-muted small fst-italic mt-1">(Please write about the author within given characters — {handleGetTranslationByLang(selectedLang).about?.length || 0}/4500)</span>
              </label>
              <textarea
                className="form_control"
                placeholder={`${t("general.enter")} ${t("general.about")}...`}
                type="text"
                name="about"
                maxLength={4500}
                value={handleGetTranslationByLang(selectedLang).about}
                onChange={(e) => handleUpdateTranslationField(e.target.name, e.target.value, selectedLang)}
                style={{ height: "340px" }}
              // Pasted markdown markers ("**The Internet of Money**") are stripped:
              // this field stores plain text, so they would be saved and shown
              // literally.
              onPaste={(event) =>
                pastePlainText(event, (value) =>
                  handleUpdateTranslationField("about", value, selectedLang)
                )
              }
              />
              {errors && errors.about && <p className="error-message d-block">{errors.about}</p>}
            </div>
             <div className="col-sm-12 col-md-4 mb-3">
              <PreviewImage
                label={t("general.coverImage")}
                uploadMedia={(e) => handleUploadMedia(e)}
                image={values.image?.uploadedImageUrl}
                mediaError={editMediaError}
                error={errors?.image || ""}
              />
            </div>
          </div>
        </div>
        </EditLoadGate>

        <div className="d-flex gap-2 justify-content-end mt-3">
          <button type="button" className="btn btn-outline-secondary" onClick={handleClose} disabled={isSubmit}>Cancel</button>
          <Button onClick={handleAddAndUpdateValues} disabled={isSubmit || !editReady} isSubmit={isSubmit} label="Submitting ..." text={isEditAuthorModalVisible ? "Update" : submitLabel("authors")} />
        </div>
      </div>
    </Modal>

    {isBulkUploadAuthorPopupVisible && (
      <BulkUploadAuthor
        isPopupVisible={isBulkUploadAuthorPopupVisible}
        handleClosePopup={handleCloseBulkUploadAuthorPopup}
        onSuccess={onSuccess}
      />
    )}
    </React.Fragment>
  );
};

export default AddEditPopup;
